import React from 'react';
import { Link } from 'react-router-dom';
import { ClompApi } from '../api/ClompApi';

// Signing up for the bingo event, and the live draft that puts the players on teams. Before the event starts a player
// (logged in with clomp) signs up with the RuneScape account they will play on. An admin makes a captain of each team
// and sets the order they pick in; captains then take turns picking the players, snaking back each round (the last to
// pick in one round picks first in the next). A pick puts the player on the team, so their drops go in for it.

const api = new ClompApi();

export const ACCOUNT_TYPES = [
    { id: 'main', name: 'Main', short: 'Main' },
    { id: 'ironman', name: 'Ironman', short: 'Iron' },
    { id: 'hardcore', name: 'Hardcore Ironman', short: 'HCIM' },
    { id: 'ultimate', name: 'Ultimate Ironman', short: 'UIM' },
    { id: 'group', name: 'Group Ironman', short: 'GIM' }
];

// PvM ability, 1 to 4
export const PVM_LEVELS = [
    { name: 'Beginner', hint: 'new to bossing' },
    { name: 'Intermediate', hint: 'most mid-level bosses' },
    { name: 'Advanced', hint: 'raids and harder bosses' },
    { name: 'Expert', hint: 'high-level raids, grandmaster combat tasks' }
];

const COMMENTS_MAX = 300;

const accountShort = (id) => (ACCOUNT_TYPES.find((type) => type.id === id) || { short: id }).short;
const accountName = (id) => (ACCOUNT_TYPES.find((type) => type.id === id) || { name: id }).name;
const pvmName = (level) => (PVM_LEVELS[level - 1] || { name: '?' }).name;
const hours = (playtime) => `${playtime % 1 === 0 ? playtime : playtime.toFixed(1)}h`;
const sameName = (a, b) => Boolean(a && b) && a.toLowerCase() === b.toLowerCase();

// What to say for an error from the backend
const problem = (err, fallback) => (err.response && err.response.data && err.response.data.message) || fallback;

// The table of players signed up: their RuneScape account, and what each one brings. columns adds to each row:
// before (a cell first, e.g. the pick number) and after (a cell last, e.g. a Pick button), both functions of a sign-up.
function PlayerTable({ signups, teams, showComments, before, after, beforeHead, afterHead, highlight }) {
    let teamOf = (signup) => teams.find((team) => (team.members || []).some((member) => member.id === signup.user.id));
    return (
        <div className="bingo-signup-table-wrap">
            <table className="bingo-signup-table">
                <thead>
                    <tr>
                        {before ? <th>{beforeHead}</th> : null}
                        <th>Player <span className="bingo-signup-sub">in-game · clomp</span></th>
                        <th className="num">Total</th>
                        <th className="num">Combat</th>
                        <th>Account</th>
                        <th className="num">Daily</th>
                        <th>PvM</th>
                        <th>Captain?</th>
                        {teams.length > 0 ? <th>Team</th> : null}
                        {showComments ? <th>Comments</th> : null}
                        {after ? <th>{afterHead}</th> : null}
                    </tr>
                </thead>
                <tbody>
                    {signups.map((signup) => {
                        let team = teamOf(signup);
                        return (
                            <tr key={signup.user.id} className={highlight && highlight(signup) ? 'is-highlighted' : ''}>
                                {before ? <td>{before(signup)}</td> : null}
                                <td className="bingo-signup-who">
                                    <strong>{signup.rsn}</strong>
                                    <Link to={`/profile/${encodeURIComponent(signup.user.username)}`}>{signup.user.username}</Link>
                                </td>
                                <td className="num">{signup.totalLevel}</td>
                                <td className="num">{signup.combatLevel}</td>
                                <td title={accountName(signup.accountType)}>{accountShort(signup.accountType)}</td>
                                <td className="num">{hours(signup.playtime)}</td>
                                <td>{pvmName(signup.pvm)}</td>
                                <td>{signup.captain ? <span className="bingo-signup-yes">Yes</span> : <span className="bingo-signup-no">No</span>}</td>
                                {teams.length > 0 ?
                                    <td>{team ? <span className="bingo-signup-team" style={{ '--team': team.color }}><span className="bingo-team-swatch" aria-hidden="true" />{team.name}</span> : <span className="bingo-signup-no">-</span>}</td>
                                    : null}
                                {showComments ? <td className="bingo-signup-comments">{signup.comments || ''}</td> : null}
                                {after ? <td className="bingo-signup-after">{after(signup)}</td> : null}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// The Sign-ups tab: signing up (or a prompt to make an account first), and everyone signed up so far.
// props: account ({ username } or null), accountChecked, data ({ open, signups, mine } or null while loading),
// admin (whether the admin controls show), teams, startsAt, onChanged (the sign-ups changed: load them again)
export class BingoSignups extends React.Component {

    state = {
        formOpen: false,
        form: null,
        saving: false,
        error: null,
        confirmingWithdraw: false,
        confirmingRemove: null
    };

    openForm = () => {
        let mine = this.props.data && this.props.data.mine;
        this.setState({
            formOpen: true,
            error: null,
            form: mine ?
                { rsn: mine.rsn, totalLevel: String(mine.totalLevel), combatLevel: String(mine.combatLevel), accountType: mine.accountType,
                    playtime: String(mine.playtime), pvm: String(mine.pvm), captain: mine.captain ? 'yes' : 'no', comments: mine.comments || '' }
                : { rsn: '', totalLevel: '', combatLevel: '', accountType: '', playtime: '', pvm: '', captain: '', comments: '' }
        });
    }

    send = (e) => {
        e.preventDefault();
        let { form } = this.state;
        let whole = (text) => (/^\d+$/.test(text.trim()) ? Number(text.trim()) : NaN);
        let missing = [['rsn', 'your OSRS username'], ['totalLevel', 'your total level'], ['combatLevel', 'your combat level'], ['accountType', 'your account type'],
            ['playtime', 'your daily playtime'], ['pvm', 'your PvM ability'], ['captain', 'whether you would captain a team']].find(([key]) => !String(form[key]).trim());
        if (missing) {
            this.setState({ error: `Fill in ${missing[1]}` });
            return;
        }
        let playtime = Number(form.playtime.trim());
        this.setState({ saving: true, error: null });
        api.bingo.signUp({
            rsn: form.rsn.trim(), totalLevel: whole(form.totalLevel), combatLevel: whole(form.combatLevel), accountType: form.accountType,
            playtime: isNaN(playtime) ? -1 : playtime, pvm: Number(form.pvm), captain: form.captain === 'yes', comments: form.comments.trim()
        }).then(() => {
            this.setState({ saving: false, formOpen: false });
            this.props.onChanged();
        }).catch((err) => this.setState({ saving: false, error: problem(err, "Couldn't save your sign-up. Please try again.") }));
    }

    withdraw = () => {
        this.setState({ saving: true, error: null });
        api.bingo.withdraw().then(() => {
            this.setState({ saving: false, confirmingWithdraw: false });
            this.props.onChanged();
        }).catch((err) => this.setState({ saving: false, confirmingWithdraw: false, error: problem(err, "Couldn't take back your sign-up. Please try again.") }));
    }

    remove(signup) {
        this.setState({ saving: true, error: null });
        api.bingo.removeSignup(signup.user.id).then(() => {
            this.setState({ saving: false, confirmingRemove: null });
            this.props.onChanged();
        }).catch((err) => this.setState({ saving: false, confirmingRemove: null, error: problem(err, "Couldn't remove that sign-up. Please try again.") }));
    }

    // The top of the tab: what a visitor can do about signing up
    renderCall() {
        let { account, accountChecked, data, startsAt } = this.props;
        let { saving, confirmingWithdraw } = this.state;
        if (!data || !accountChecked) {
            return null;
        }
        let until = startsAt ? ` until the event starts on ${new Date(startsAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}` : '';
        if (!data.open) {
            return (
                <div className="bingo-signup-call">
                    <p>Sign-ups closed when the event started.{data.mine ? <> You signed up as <strong>{data.mine.rsn}</strong>.</> : null}</p>
                </div>
            );
        }
        if (!account) {
            return (
                <div className="bingo-signup-call is-open">
                    <div>
                        <h3>Sign-ups are open{until}</h3>
                        <p>To sign up you need a clomp account. Make one (it's free), and you'll come straight back here to sign up.</p>
                    </div>
                    <div className="bingo-signup-call-actions">
                        <Link className="bingo-btn bingo-btn-primary" to="/register?next=/bingo">Create an account</Link>
                        <Link className="bingo-btn bingo-btn-ghost" to="/login?next=/bingo">Log in</Link>
                    </div>
                </div>
            );
        }
        if (data.mine) {
            return (
                <div className="bingo-signup-call is-done">
                    <div>
                        <h3>You're signed up</h3>
                        <p>As <strong>{data.mine.rsn}</strong>: {data.mine.totalLevel} total, {data.mine.combatLevel} combat, {accountName(data.mine.accountType)}. You can change this{until}.</p>
                    </div>
                    <div className="bingo-signup-call-actions">
                        {confirmingWithdraw ?
                            <>
                                <span className="bingo-confirm">Take back your sign-up?</span>
                                <button type="button" className="bingo-btn bingo-btn-danger bingo-btn-small" disabled={saving} onClick={this.withdraw}>Yes, take it back</button>
                                <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => this.setState({ confirmingWithdraw: false })}>Keep it</button>
                            </>
                            :
                            <>
                                <button type="button" className="bingo-btn bingo-btn-primary" onClick={this.openForm}>Edit sign-up</button>
                                <button type="button" className="bingo-btn bingo-btn-ghost" onClick={() => this.setState({ confirmingWithdraw: true, error: null })}>Withdraw</button>
                            </>}
                    </div>
                </div>
            );
        }
        return (
            <div className="bingo-signup-call is-open">
                <div>
                    <h3>Sign-ups are open{until}</h3>
                    <p>Tell us about the RuneScape account you'll play on. Captains pick their teams from everyone signed up in a live draft.</p>
                </div>
                <div className="bingo-signup-call-actions">
                    <button type="button" className="bingo-btn bingo-btn-primary" onClick={this.openForm}>Sign up for the event</button>
                </div>
            </div>
        );
    }

    renderForm() {
        let { form, saving, error } = this.state;
        let editing = Boolean(this.props.data && this.props.data.mine);
        let close = () => this.setState({ formOpen: false });
        let field = (key) => ({ value: form[key], onChange: (e) => this.setState({ form: { ...form, [key]: e.target.value }, error: null }) });
        return (
            <div className="bingo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { close(); } }}
                onKeyDown={(e) => { if (e.key === 'Escape') { close(); } }}>
                <form className="bingo-tile-form bingo-submit-form bingo-signup-form" role="dialog" aria-modal="true" aria-labelledby="bingo-signup-title" onSubmit={this.send}>
                    <h2 id="bingo-signup-title">{editing ? 'Your sign-up' : 'Sign up for the event'}</h2>
                    <label htmlFor="bingo-signup-rsn">OSRS username <span className="bingo-optional">(in-game name, can differ from your clomp name)</span></label>
                    <input id="bingo-signup-rsn" type="text" maxLength={12} autoComplete="off" autoFocus placeholder="The in-game name of the account you'll play on" {...field('rsn')} />
                    <div className="bingo-signup-pair">
                        <div>
                            <label htmlFor="bingo-signup-total">Total level</label>
                            <input id="bingo-signup-total" type="number" inputMode="numeric" min={32} max={2376} placeholder="e.g. 1850" {...field('totalLevel')} />
                        </div>
                        <div>
                            <label htmlFor="bingo-signup-combat">Combat level</label>
                            <input id="bingo-signup-combat" type="number" inputMode="numeric" min={3} max={126} placeholder="3 to 126" {...field('combatLevel')} />
                        </div>
                    </div>
                    <label htmlFor="bingo-signup-type">Account type</label>
                    <select id="bingo-signup-type" {...field('accountType')}>
                        <option value="">Choose</option>
                        {ACCOUNT_TYPES.map((type) => <option key={type.id} value={type.id}>{type.name}{type.short !== type.name && type.short !== 'Iron' ? ` (${type.short})` : ''}</option>)}
                    </select>
                    <div className="bingo-signup-pair">
                        <div>
                            <label htmlFor="bingo-signup-playtime">Daily playtime <span className="bingo-optional">(hours, about)</span></label>
                            <input id="bingo-signup-playtime" type="number" inputMode="decimal" min={0} max={24} step={0.5} placeholder="e.g. 3" {...field('playtime')} />
                        </div>
                        <div>
                            <label htmlFor="bingo-signup-pvm">PvM ability</label>
                            <select id="bingo-signup-pvm" {...field('pvm')}>
                                <option value="">Choose</option>
                                {PVM_LEVELS.map((level, k) => <option key={k} value={k + 1}>{level.name}: {level.hint}</option>)}
                            </select>
                        </div>
                    </div>
                    <fieldset className="bingo-signup-captain">
                        <legend>Willing to be a team captain?</legend>
                        {[['yes', 'Yes'], ['no', 'No']].map(([value, name]) => (
                            <label key={value} className="bingo-check">
                                <input type="radio" name="bingo-signup-captain" value={value} checked={form.captain === value}
                                    onChange={() => this.setState({ form: { ...form, captain: value }, error: null })} />
                                {name}
                            </label>
                        ))}
                    </fieldset>
                    <label htmlFor="bingo-signup-comments">Other comments <span className="bingo-optional">(optional)</span></label>
                    <textarea id="bingo-signup-comments" rows="3" maxLength={COMMENTS_MAX} placeholder="Times you can't play, who you'd like to team with, anything else"
                        {...field('comments')} />
                    {error ? <p className="bingo-error" role="alert">{error}</p> : null}
                    <div className="bingo-tile-form-actions">
                        <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving} onClick={close}>Cancel</button>
                        <button type="submit" className="bingo-btn bingo-btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Save' : 'Sign up'}</button>
                    </div>
                </form>
            </div>
        );
    }

    render() {
        let { data, admin, teams, account } = this.props;
        let { formOpen, error, confirmingRemove, saving } = this.state;
        let signups = data ? data.signups : [];
        let showComments = signups.some((signup) => signup.comments !== undefined);
        return (
            <section className="bingo-signups" aria-label="Sign-ups">
                {this.renderCall()}
                {error && !formOpen ? <p className="bingo-error" role="alert">{error}</p> : null}
                <div className="bingo-panel-head">
                    <h2>Signed up <span>{data ? `${signups.length} player${signups.length === 1 ? '' : 's'}` : ''}</span></h2>
                </div>
                {!data ?
                    <p className="bingo-status">Loading the sign-ups...</p>
                    : signups.length === 0 ?
                        <p className="bingo-hint">Nobody has signed up yet.</p>
                        :
                        <PlayerTable signups={signups} teams={teams} showComments={showComments}
                            highlight={(signup) => account && sameName(signup.user.username, account.username)}
                            afterHead={admin ? <span className="sr-only">Remove</span> : null}
                            after={admin ? (signup) => (confirmingRemove === signup.user.id ?
                                <span className="bingo-signup-confirm">
                                    <button type="button" className="bingo-btn bingo-btn-danger bingo-btn-small" disabled={saving} onClick={() => this.remove(signup)}>Remove</button>
                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => this.setState({ confirmingRemove: null })}>Keep</button>
                                </span>
                                :
                                <button type="button" className="bingo-signup-remove" aria-label={`Remove ${signup.rsn}'s sign-up`} title="Remove this sign-up"
                                    onClick={() => this.setState({ confirmingRemove: signup.user.id, error: null })}>✕</button>) : null} />}
                {formOpen ? this.renderForm() : null}
            </section>
        );
    }

}

// The Draft tab. props: draft, teams, signups (the sign-ups data, or null), account, admin, saving, error,
// change(send) (send a change that answers with the event, and show it)
export class BingoDraft extends React.Component {

    state = {
        // the player whose pick waits to be confirmed (user id)
        confirmingPick: null,
        confirmingReset: false,
        sort: 'total'
    };

    componentDidUpdate(previous) {
        // a pick made elsewhere moves the draft on: a pick waiting to be confirmed is for a turn that has gone
        let number = (draft) => (draft.onClock ? draft.onClock.number : -1);
        if (number(previous.draft) !== number(this.props.draft) && this.state.confirmingPick !== null) {
            this.setState({ confirmingPick: null });
        }
    }

    // The teams in the order they pick in, in the first round
    ordered() {
        let { draft, teams } = this.props;
        return draft.order.map((id) => teams.find((team) => team.id === id)).filter(Boolean);
    }

    move(k, by) {
        let order = this.ordered().map((team) => team.id);
        [order[k], order[k + by]] = [order[k + by], order[k]];
        this.props.change(() => api.bingo.setDraftOrder(order));
    }

    shuffle = () => {
        let order = this.ordered().map((team) => team.id);
        for (let k = order.length - 1; k > 0; k--) {
            let j = Math.floor(Math.random() * (k + 1));
            [order[k], order[j]] = [order[j], order[k]];
        }
        this.props.change(() => api.bingo.setDraftOrder(order));
    }

    // The admins' set-up before the draft: each team's captain, and the order they pick in
    renderSetup(signups) {
        let { saving, change } = this.props;
        let teams = this.ordered();
        let captainChoices = [...signups].sort((a, b) => Number(b.captain) - Number(a.captain) || a.rsn.localeCompare(b.rsn));
        let ready = teams.length > 0 && teams.every((team) => team.captain);
        return (
            <div className="bingo-draft-setup">
                <p className="bingo-hint">Choose a captain for each team (players who said they'd captain are marked ★), and the order the teams pick in. The order snakes back each round.</p>
                {teams.length === 0 ? <p className="bingo-hint">Add the teams first, with Edit teams on the Board tab.</p> : null}
                <ol className="bingo-draft-order">
                    {teams.map((team, k) => (
                        <li key={team.id} style={{ '--team': team.color }}>
                            <span className="bingo-draft-place">{k + 1}</span>
                            <span className="bingo-draft-move">
                                <button type="button" aria-label={`Move ${team.name} up`} disabled={saving || k === 0} onClick={() => this.move(k, -1)}>▲</button>
                                <button type="button" aria-label={`Move ${team.name} down`} disabled={saving || k === teams.length - 1} onClick={() => this.move(k, 1)}>▼</button>
                            </span>
                            <span className="bingo-team-swatch" aria-hidden="true" />
                            <span className="bingo-draft-team-name">{team.name}</span>
                            <label className="sr-only" htmlFor={`bingo-captain-${team.id}`}>Captain of {team.name}</label>
                            <select id={`bingo-captain-${team.id}`} className="bingo-draft-captain" disabled={saving}
                                value={team.captain ? String(team.captain.id) : ''}
                                onChange={(e) => change(() => api.bingo.setCaptain(team.id, e.target.value ? Number(e.target.value) : null))}>
                                <option value="">No captain yet</option>
                                {team.captain && !signups.some((signup) => signup.user.id === team.captain.id) ?
                                    <option value={team.captain.id}>{team.captain.username}</option> : null}
                                {captainChoices.map((signup) => (
                                    <option key={signup.user.id} value={signup.user.id}>{signup.captain ? '★ ' : ''}{signup.rsn} ({signup.user.username})</option>
                                ))}
                            </select>
                        </li>
                    ))}
                </ol>
                <div className="bingo-draft-actions">
                    <button type="button" className="bingo-btn bingo-btn-ghost" disabled={saving || teams.length < 2} onClick={this.shuffle}>Shuffle the order</button>
                    <button type="button" className="bingo-btn bingo-btn-primary" disabled={saving || !ready} onClick={() => change(() => api.bingo.draftAction('start'))}>Start the draft</button>
                    {!ready && teams.length > 0 ? <span className="bingo-optional">Every team needs a captain first</span> : null}
                </div>
            </div>
        );
    }

    // The board of picks: a column for each team (in pick order), a row for each round
    renderBoard(rsnOf) {
        let { draft } = this.props;
        let teams = this.ordered();
        if (teams.length === 0) {
            return null;
        }
        // where each pick lands: round by round, snaking
        let cells = {};
        draft.picks.forEach((pick, n) => {
            let round = Math.floor(n / teams.length);
            cells[`${round}:${pick.teamId}`] = { ...pick, n: n + 1 };
        });
        let clock = draft.onClock;
        let rounds = Math.max(1, Math.ceil((draft.picks.length + (clock ? 1 : 0)) / teams.length));
        return (
            <div className="bingo-draft-board-wrap">
                <table className="bingo-draft-board">
                    <thead>
                        <tr>
                            <th className="bingo-draft-round"><span className="sr-only">Round</span></th>
                            {teams.map((team) => (
                                <th key={team.id} style={{ '--team': team.color }}>
                                    <span className="bingo-draft-board-team"><span className="bingo-team-swatch" aria-hidden="true" />{team.name}</span>
                                    <small>{team.captain ? `★ ${rsnOf(team.captain)}` : 'no captain'}</small>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: rounds }, (_, round) => (
                            <tr key={round}>
                                <th className="bingo-draft-round" scope="row">R{round + 1} <span aria-hidden="true">{round % 2 === 0 ? '→' : '←'}</span></th>
                                {teams.map((team) => {
                                    let cell = cells[`${round}:${team.id}`];
                                    let now = clock && clock.round === round + 1 && clock.teamId === team.id;
                                    return (
                                        <td key={team.id} className={now ? 'is-now' : ''} style={{ '--team': team.color }}>
                                            {cell ? <><small>#{cell.n}</small> {rsnOf(cell.user)}</> : now ? <span className="bingo-draft-picking">Picking...</span> : null}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        let { draft, teams, signups: data, account, admin, saving, error, change } = this.props;
        let { confirmingPick, confirmingReset, sort } = this.state;
        let signups = data ? data.signups : [];
        let rsnOf = (user) => {
            let signup = signups.find((other) => other.user.id === user.id);
            return signup ? signup.rsn : user.username;
        };
        let taken = new Set(teams.flatMap((team) => (team.members || []).map((member) => member.id)));
        let left = signups.filter((signup) => !taken.has(signup.user.id));
        let sorts = {
            total: (a, b) => b.totalLevel - a.totalLevel,
            combat: (a, b) => b.combatLevel - a.combatLevel,
            pvm: (a, b) => b.pvm - a.pvm || b.totalLevel - a.totalLevel,
            playtime: (a, b) => b.playtime - a.playtime,
            name: (a, b) => a.rsn.localeCompare(b.rsn)
        };
        left = [...left].sort(sorts[sort]);
        let clock = draft.onClock;
        let clockTeam = clock ? teams.find((team) => team.id === clock.teamId) : null;
        let myPick = Boolean(clockTeam && clockTeam.captain && account && sameName(clockTeam.captain.username, account.username));
        let canPick = Boolean(clock && (myPick || admin));
        let status = { off: 'Not started', live: 'Live', done: 'Finished' }[draft.status];
        return (
            <section className="bingo-draft" aria-label="Draft">
                <div className="bingo-panel-head">
                    <h2>Team draft <span className={`bingo-draft-status is-${draft.status}`}>{status}</span></h2>
                </div>
                {error ? <p className="bingo-error" role="alert">{error}</p> : null}
                {draft.status === 'off' ?
                    admin ? this.renderSetup(signups) :
                        <div className="bingo-draft-waiting">
                            <p>Captains will pick their teams from everyone signed up, in a live snake draft. Check back here when it starts.</p>
                            {teams.some((team) => team.captain) ?
                                <ul className="bingo-draft-captains">
                                    {this.ordered().map((team) => (
                                        <li key={team.id} style={{ '--team': team.color }}><span className="bingo-team-swatch" aria-hidden="true" />{team.name}: {team.captain ? <strong>{rsnOf(team.captain)}</strong> : 'captain to come'}</li>
                                    ))}
                                </ul>
                                : null}
                        </div>
                    :
                    <>
                        {clock && clockTeam ?
                            <div className={'bingo-draft-clock' + (myPick ? ' is-mine' : '')} style={{ '--team': clockTeam.color }} role="status">
                                <span className="bingo-draft-clock-round">Round {clock.round} · Pick {clock.pick} of {teams.length}</span>
                                <span className="bingo-draft-clock-team">
                                    <span className="bingo-team-swatch" aria-hidden="true" />
                                    <span>{myPick ? <>It's your pick for <strong>{clockTeam.name}</strong>!</> : <><strong>{clockTeam.name}</strong> {clockTeam.captain ? `(${rsnOf(clockTeam.captain)})` : ''} is picking</>}</span>
                                </span>
                            </div>
                            : null}
                        {draft.status === 'done' ? <p className="bingo-done" role="status">The draft is over: the teams are set. Good luck!</p> : null}
                        {this.renderBoard(rsnOf)}
                        {admin ?
                            <div className="bingo-draft-actions">
                                <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving || draft.picks.length === 0}
                                    onClick={() => change(() => api.bingo.draftAction('undo'))}>Undo last pick</button>
                                {draft.status === 'live' ?
                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => change(() => api.bingo.draftAction('end'))}>End the draft</button>
                                    : null}
                                {confirmingReset ?
                                    <>
                                        <span className="bingo-confirm">Take back every pick and start over?</span>
                                        <button type="button" className="bingo-btn bingo-btn-danger bingo-btn-small" disabled={saving}
                                            onClick={() => { this.setState({ confirmingReset: false }); change(() => api.bingo.draftAction('reset')); }}>Yes, reset</button>
                                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => this.setState({ confirmingReset: false })}>Keep</button>
                                    </>
                                    :
                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => this.setState({ confirmingReset: true })}>Reset the draft</button>}
                            </div>
                            : null}
                    </>}
                {draft.status !== 'off' || admin ?
                    <>
                        <div className="bingo-panel-head bingo-draft-left-head">
                            <h2>Still to pick <span>{left.length} player{left.length === 1 ? '' : 's'}</span></h2>
                            {left.length > 1 ?
                                <label className="bingo-draft-sort">Sort by{' '}
                                    <select value={sort} onChange={(e) => this.setState({ sort: e.target.value })}>
                                        <option value="total">Total level</option>
                                        <option value="combat">Combat level</option>
                                        <option value="pvm">PvM ability</option>
                                        <option value="playtime">Daily playtime</option>
                                        <option value="name">Name</option>
                                    </select>
                                </label>
                                : null}
                        </div>
                        {left.length === 0 ?
                            <p className="bingo-hint">{data ? 'Everyone signed up is on a team.' : 'Loading the sign-ups...'}</p>
                            :
                            <PlayerTable signups={left} teams={[]} showComments={left.some((signup) => signup.comments !== undefined)}
                                highlight={(signup) => confirmingPick === signup.user.id}
                                afterHead={canPick ? <span className="sr-only">Pick</span> : null}
                                after={canPick ? (signup) => (confirmingPick === signup.user.id ?
                                    <span className="bingo-signup-confirm">
                                        <button type="button" className="bingo-btn bingo-btn-primary bingo-btn-small" disabled={saving}
                                            onClick={() => { this.setState({ confirmingPick: null }); change(() => api.bingo.pick(signup.user.id, clock.number)); }}>
                                            Pick for {clockTeam.name}
                                        </button>
                                        <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving} onClick={() => this.setState({ confirmingPick: null })}>Cancel</button>
                                    </span>
                                    :
                                    <button type="button" className="bingo-btn bingo-btn-ghost bingo-btn-small" disabled={saving}
                                        onClick={() => this.setState({ confirmingPick: signup.user.id })}>Pick</button>) : null} />}
                    </>
                    : null}
            </section>
        );
    }

}
